const THREE = (window.THREE = require('three'));
require('three/examples/js/loaders/GLTFLoader');
require('../../../../../../node_modules/three/examples/js/modifiers/SubdivisionModifier');

import anime from 'animejs';

export class SpaceDroidScene {
  /**
   * @param {DOM Element} element
   */
  constructor(element) {
    // We will use this reference to manipulate the DOM.
    this.rootElement = element;

    // Setup the WebGL scene.
    this.scene = new THREE.Scene();

    // Our camera points towards the origin straight-on.
    const aspectRatio = element.offsetWidth / element.offsetHeight;
    this.camera = new THREE.PerspectiveCamera(35, aspectRatio, 0.1, 1000);
    this.camera.position.set(0, 0, 35);
    this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    this.scene.add(this.camera);

    // Our key light is at a 3/4 angle from our subject.
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.75);
    this.directionalLight.position.set(-1, 1, 5);
    this.scene.add(this.directionalLight);

    // Our fill light normalizes the colors, making white 100% white.
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.25);
    this.scene.add(this.ambientLight);

    // Inject to the DOM.
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(element.offsetWidth, element.offsetHeight);
    this.renderer.domElement.className = 'space-droid__canvas';
    this.renderer.domElement.style.opacity = 1;
    element.appendChild(this.renderer.domElement);

    // When loading external assets, this will help us know when
    // everything is loaded.
    this.loadingManager = new THREE.LoadingManager();
    this.loadingManager.onLoad = this.onLoad.bind(this);

    // Load the Android model by fetching it from the server.
    const loader = new THREE.GLTFLoader(this.loadingManager);
    loader.load('models/Aldulain_Logo.glb', (object) => {
      //const loader = new THREE.ObjectLoader(this.loadingManager);
      //loader.load('models/android.json', (object) => {
      // Save a reference to the loaded android model.
      this.android = object;

      // Make materials here in three.js.
      const blueMaterial = new THREE.MeshToonMaterial({
        color: new THREE.Color(0x0000c5),
        //flatShading: THREE.SmoothShading,
        shininess: 60,
      });

      const redMaterial = new THREE.MeshToonMaterial({
        color: new THREE.Color(0xc90000),
        //flatShading: THREE.SmoothShading,
        shininess: 15,
      });

      const whiteMaterial = new THREE.MeshToonMaterial({
        color: new THREE.Color(0xffffff),
        shininess: 15,
        //flatShading: THREE.SmoothShading,
      });

      // Apply materials and subdivision to every piece of geometry
      // within the loaded model.
      for (let mesh of this.android.scene.children) {
        switch (mesh.material.name) {
          // These names are what they were called in Blender prior to
          // export. We'll use them now to identify what their materials
          // should be.
          case 'blueMaterial':
            mesh.material = blueMaterial;
            break;
          case 'redMaterial':
            mesh.material = redMaterial;
            break;
          case 'whiteMaterial':
            mesh.material = whiteMaterial;
            break;
        }
        for (let childMesh of mesh.children) {
          switch (childMesh.material.name) {
            // These names are what they were called in Blender prior to
            // export. We'll use them now to identify what their materials
            // should be.
            case 'blueMaterial':
              childMesh.material = blueMaterial;
              break;
            case 'redMaterial':
              childMesh.material = redMaterial;
              break;
            case 'whiteMaterial':
              childMesh.material = whiteMaterial;
              break;
          }
        }
      }

      // Nest our android inside a new object so we can manipulate an
      // extra set of axes while the android spins on its own axes.
      this.androidGroup = new THREE.Object3D();
      this.androidGroup.position.set(0, 0, 0);
      this.androidGroup.rotation.set(deg(0), deg(0), deg(0));
      this.androidGroup.add(this.android.scene);
      this.android.scene.position.set(0, 0, 0);
      this.android.scene.rotation.set(deg(2), deg(0), deg(-1));
      this.scene.add(this.androidGroup);
    });
  }

  /**
   * Called once when all the assets are finished loading.
   */
  onLoad() {
    // Render a single frame.
    //this.renderer.render(this.scene, this.camera);
    // Establish a new subset of properties.
    //set maximums
    this.android.custom = {
      rotationSpeedX: 0.002,
      rotationSpeedY: 0.002,
      rotationSpeedZ: 0.002,
      rotationMinMax: {
        minX: deg(-20),
        minY: deg(-20),
        minZ: deg(-5),
        maxX: deg(20),
        maxY: deg(20),
        maxZ: deg(10),
      },
    };
    /**
     * Called on every frame refresh (up to 60 fps on most screens).
     *
     * @param {number} timestamp - Milliseconds since page has loaded.
     * @return {void}
     */
    const renderFrame = function(timestamp) {
      // Like setTimeout, but waits for the next available browser
      // repaint.
      requestAnimationFrame(renderFrame);

      // Change the Android's local rotation by some fixed increment
      // every frame. Be aware that frames aren't always rendered for
      // various reasons (window is minimized), so it'll pick up
      // wherever it left off on the next render.
      if (
        this.android.scene.rotation.x < this.android.custom.rotationMinMax.minX ||
        this.android.scene.rotation.x > this.android.custom.rotationMinMax.maxX
      ) {
        this.android.custom.rotationSpeedX *= -1;
      }

      if (
        this.android.scene.rotation.y < this.android.custom.rotationMinMax.minY ||
        this.android.scene.rotation.y > this.android.custom.rotationMinMax.maxY
      ) {
        this.android.custom.rotationSpeedY *= -1;
      }

      if (
        this.android.scene.rotation.z < this.android.custom.rotationMinMax.minZ ||
        this.android.scene.rotation.z > this.android.custom.rotationMinMax.maxZ
      ) {
        this.android.custom.rotationSpeedZ *= -1;
      }

      this.android.scene.rotation.x += parseFloat(this.android.custom.rotationSpeedX);
      this.android.scene.rotation.y += parseFloat(this.android.custom.rotationSpeedY);
      this.android.scene.rotation.z += parseFloat(this.android.custom.rotationSpeedZ);

      // Render the new frame.
      this.renderer.render(this.scene, this.camera);
    }.bind(this);

    // It's important to render the first frame inside a
    // requestAnimationFrame call in order to avoid some startup lag.
    requestAnimationFrame(renderFrame);
  }
}

/**
 * Helper to random element of an array
 * @param {Array} array
 */
function chooseRandom(array) {
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Helper to convert degrees to radians.
 * @param {Number} degrees
 */
function deg(degrees) {
  return (Math.PI * degrees) / 180;
}
